import { type JSX, useEffect, useState } from "react";

import { type LookupItem, type LookupKind, fetchLookup } from "../api/lookup";

interface LookupFieldProps {
  kind: LookupKind;
  value: string;
  onChange: (value: string) => void;
  label?: string | undefined;
  required?: boolean;
}

/**
 * Pick an entity, memory or repository by name instead of pasting a ULID.
 *
 * Free text stays available: a tester with an id in hand from somewhere else must not be forced
 * through the picker. The selected id is what gets submitted; the name is only for recognising it.
 */
export function LookupField({
  kind,
  value,
  onChange,
  label,
  required = false,
}: LookupFieldProps): JSX.Element {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<LookupItem[]>([]);
  const [detail, setDetail] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    const controller = new AbortController();
    // Debounced: a keystroke per request would hammer the service on every letter.
    const timer = window.setTimeout(() => {
      setLoading(true);
      void fetchLookup(kind, search, controller.signal)
        .then((response) => {
          if (!controller.signal.aborted) {
            setItems(response.items);
            setDetail(response.detail);
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setDetail("The lookup could not run. Enter the id directly.");
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setLoading(false);
          }
        });
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [kind, search, open]);

  const selected = items.find((item) => item.id === value);

  return (
    <div className="lookup">
      {label && (
        <span className="lookup__label">
          {label} {required && <em>required</em>}
        </span>
      )}

      <span className="input-row">
        <input
          value={value}
          placeholder={`${kind} id`}
          onChange={(event) => onChange(event.target.value)}
          aria-label={label ?? `${kind} id`}
        />
        <button type="button" onClick={() => setOpen(!open)}>
          {open ? "Close" : `Find ${kind}`}
        </button>
      </span>

      {selected && <small className="muted">{selected.label}</small>}

      {open && (
        <div className="lookup__panel">
          <input
            type="search"
            value={search}
            placeholder={`Search ${kind}s…`}
            aria-label={`Search ${kind}s`}
            onChange={(event) => setSearch(event.target.value)}
          />

          {loading && <p className="muted">Searching…</p>}

          {detail && <p className="muted">{detail}</p>}

          {!loading && items.length === 0 && !detail && (
            <p className="muted">
              Nothing found{search ? ` for “${search}”` : ""}. Enter the id directly if you have
              it.
            </p>
          )}

          <ul className="lookup__list">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="lookup__item"
                  onClick={() => {
                    onChange(item.id);
                    setOpen(false);
                  }}
                >
                  <span>{item.label}</span>
                  {item.detail && <small className="muted">{item.detail}</small>}
                  <code>{item.id}</code>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
